import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { getCurrentUser } from 'redux/selectors/users';

// Translations
import t from 'react-translate';

// Types
import { SessionTypes, LiveEventSession as LiveEventSessionType, ZoomSession } from 'redux/schemas/models/live-event';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';

// Styles
import { css } from '@emotion/react';
import { gray4, gray7 } from 'styles/global_defaults/colors';
import { iconSmallSize } from 'styles/global_defaults/icons';
import { getSanitizedStyles } from 'shared/utils';

// Components
import NvFroala from 'froala/components/nv-froala';
import NvDatePicker, { DatePickerType, getMomentDate } from 'shared/components/inputs/nv-datepicker';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { FormNvClickToEdit, InputType } from 'components/nv-click-to-edit';
import NvFormDropdown from 'shared/components/inputs/nv-form-dropdown';
import NvTimezoneSelector from 'shared/components/nv-timezone-selector';
import ZoomHostValidator from './zoom-host-validator';

// Pendo
import { config } from '../../../config/pendo.config.json';
import { LiveSessionFormContext } from './live-session-form';

const styles = css`
  background-color: ${gray7};

  .badge-index {
    width: ${iconSmallSize}px;
    height: ${iconSmallSize}px;
    border-radius: 10px;
    color: white;
  }

  .fr-wrapper {
    .fr-element {
      max-height: 55px;
      min-height: unset !important;
      outline: none;
    }
  }

  .timing {
    gap: 10px;
    .timing-start-time {
      flex-grow: 1;
      .react-datepicker-wrapper {
        width: 100%;
      }
    }
    .timing-timezone {
      width: 150px;
    }
    .timing-duration {
      width: 130px;
    }
  }
  .timing-timezone, .timing-duration {
    width: calc(100% - 140px);
    &.disabled {
      .bs4-dropdown {
        pointer-events: none;
        background-color: transparent;
        border-color: ${gray4};
        color: inherit;
      }
    }
    .timezone-selector {
      width: 100%;
      .icon-dropdown-arrow {
        transform: scale(0.75);
      }
    }
  }
  .bs4-dropdown-menu {
    max-height: 250px;
    overflow-y: scroll;
  }
  .live-event-topic {
    word-break: break-all;
  }
`;

type LiveEventSessionProps = {
  session: LiveEventSessionType
  showIndex: boolean
  canDelete: boolean
  delete: () => void
  onHostValidation?: (
    isValid: boolean,
    validHosts: { zoomHost?: string, alternativeHosts?: string },
    allValid?: boolean
  ) => void
};

const labelClass = 'text-small gray-3 m-0';

const LiveEventSession = (props: LiveEventSessionProps) => {
  const {
    session,
    showIndex,
    canDelete,
    delete: deleteSession,
    onHostValidation,
  } = props;

  const {
    sessionType,
    isContentManagementCollection,
    durations,
    getDurationText,
    showZoomHostValidator,
  } = useContext(LiveSessionFormContext);

  const [isValidHost, setIsValidHost] = useState(false);
  const [isValidAlternativeHost, setIsValidAlternativeHos] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const currentUser = useSelector(getCurrentUser);

  const validHost = useRef((session as ZoomSession).authenticationEmail);
  const validAlternativeHosts = useRef((session as ZoomSession).alternativeHosts);

  const name = `sessions.${session.index}`;
  const descriptionStyles = session.id ? css`border: 1px solid ${gray4};` : '';

  const { watch, formState, setValue, resetField } = useFormContext() || {};
  const startTime = watch?.(`${name}.startTime`);
  const zoomHost = watch?.(`${name}.authenticationEmail`);
  const alternativeHosts = watch?.(`${name}.alternativeHosts`);
  const timeZone = watch?.(`${name}.timeZone`);

  const shouldShowZoomHostValidator = showZoomHostValidator() || isValidHost;

  const inputStyles = css`
    .bs4-form-control, .live-session-description {
      background-color: ${disabled || isContentManagementCollection ? 'unset' : 'white'};
    }
    .react-datepicker-wrapper {
      input {
        pointer-events: ${disabled || isContentManagementCollection ? 'none' : 'unset'};
      }
    }
    .bs4-input-group {
      .bs4-input-group-append {
        background-color: transparent;
        .bs4-input-group-text {
          background-color: white;
        }
      }
    }
  `;

  useEffect(() => {
    if (sessionType === SessionTypes.ZOOM && (session as ZoomSession).authenticationEmail) {
      setIsValidHost(true);
    }
  }, [sessionType]);

  useEffect(() => {
    const originalStartTime = getMomentDate(session.startTime, 'L HH:mm', currentUser.timeZone)?.toString();
    // Check if the new starttime is the same as the default
    if (originalStartTime === startTime) {
      resetField(`${name}.startTime`);
    } else {
      const newDisabled = startTime && session.id && !formState.isDirty
        ? moment().isAfter(moment(startTime).subtract(10, 'minutes'))
        : false;
      setDisabled(newDisabled);
    }
  }, [startTime]);

  useEffect(() => {
    if (Array.isArray(alternativeHosts)) {
      setValue(`${name}.alternativeHosts`, alternativeHosts.join(', '));
    }
  }, [alternativeHosts]);

  // The session will be only valid if both (main and alt) of the host fields are valid
  useEffect(() => {
    onHostValidation?.(isValidHost, { zoomHost }, isValidHost && isValidAlternativeHost);
  }, [isValidHost]);

  useEffect(() => {
    onHostValidation?.(isValidAlternativeHost, { alternativeHosts }, isValidHost && isValidAlternativeHost);
  }, [isValidAlternativeHost]);

  const shouldDisableLessonLibrary = isContentManagementCollection && !!session.id;
  // Zoom host field is disabled when:
  // - `disabled` is true, or
  // - the session belongs to a content management collection (`isContentManagementCollection`), or
  // - the session has already been created (`session.id` is set) and has a configured start time (`startTime` is set)
  //   (for a session linked from the lesson library, a non-empty `startTime` indicates it has already been configured)
  const isZoomHostDisabled = disabled || isContentManagementCollection || (!!session.id && !!startTime);

  return (
    <div css={getSanitizedStyles([styles, inputStyles])} className='p-4 mb-4'>
      <div className='d-flex align-items-center mb-4'>
        {showIndex && (
          <div className='bg-gray-2 text-align-center bold badge-index mr-2'>{session.index + 1}</div>
        )}
        <NvTooltip
          text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
          placement='top'
          enabled={shouldDisableLessonLibrary}
        >
          <div className='w-100'>
            <FormNvClickToEdit
              className={`live-event-topic w-100 ${disabled ? 'disabled' : ''}`}
              name={`${name}.title`}
              type={InputType.SHORT_TEXT}
              showInputWhenEmpty
              text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_TITLE()}
              replaceText
              editable={!disabled && !shouldDisableLessonLibrary}
              textContainerClassName='text-large-body bold'
              dataQa={config.pendo.liveEvent.liveEventSession.title}
              withForm
            />
          </div>
        </NvTooltip>
      </div>
      <NvTooltip
        text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
        placement='top'
        enabled={shouldDisableLessonLibrary}
      >
        <div className='w-100'>
          <NvFroala
            withForm
            className='mb-4 live-session-description'
            css={descriptionStyles}
            preset={FroalaViewMode.NORMAL}
            uploadType={UploadType.NONE}
            name={`${name}.description`}
            max-length={sessionType === SessionTypes.ZOOM ? 2000 : -1}
            ariaLabel={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_DESCRIPTION()}
            placeholder={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ADD_DESCRIPTION()}
            isDisabled={disabled || shouldDisableLessonLibrary}
            dataQa={config.pendo.liveEvent.liveEventSession.description}
          />
        </div>
      </NvTooltip>
      <div className='timing mb-4 d-flex w-100 justify-content-between'>
        <div className='timing-start-time'>
          <label className={labelClass}>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.START_TIME()}</label>
          <NvTooltip
            text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvDatePicker
                required
                withForm
                placement='bottom'
                min={moment()}
                disabled={disabled || isContentManagementCollection}
                name={`${name}.startTime`}
                type={DatePickerType.DATETIME}
                placeholder={isContentManagementCollection
                  ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()
                  : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.START_TIME()}
                className='w-100'
                dataQa={config.pendo.liveEvent.liveEventSession.date}
                fixed
              />
            </div>
          </NvTooltip>
        </div>
        <div className={`timing-duration${disabled || isContentManagementCollection ? ' disabled' : ''}`}>
          <label className={labelClass}>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.DURATION()}</label>
          <NvTooltip
            text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvFormDropdown
                name={`${name}.duration`}
                items={durations.map((duration) => ({
                  id: duration,
                  type: 'text',
                  text: getDurationText(duration),
                  dataQa: `${config.pendo.liveEvent.liveEventSession.duration}-${duration}`,
                }))}
                isSmallSize
                disabled={disabled || isContentManagementCollection}
                title={isContentManagementCollection
                  ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()
                  : ''}
              />
            </div>
          </NvTooltip>
        </div>
      </div>
      <div className={`timing-timezone ${disabled || isContentManagementCollection ? ' disabled' : ''} mb-4`}>
        <label className={labelClass}>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.TIME_ZONE()}</label>
        <NvTooltip
          text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
          placement='top'
          enabled={isContentManagementCollection}
        >
          <div>
            <NvTimezoneSelector
              withForm
              defaultTimezone={timeZone?.id ?? currentUser.timeZone}
              name={`${name}.timeZone`}
              className='timezone-selector'
              dropdownWidth={420}
              disabled={disabled || isContentManagementCollection}
              dropdownMaxHeight={420}
            />
          </div>
        </NvTooltip>
      </div>
      {sessionType === SessionTypes.ZOOM && (
        <React.Fragment>
          {shouldShowZoomHostValidator && (
            <ZoomHostValidator
              formName={`${name}.authenticationEmail`}
              onValidHost={(isValid) => setIsValidHost(isValid)}
              disabled={isZoomHostDisabled}
              host={validHost.current}
              label={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_MEETING_HOST()}
              tooltip={isContentManagementCollection ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP() : ''}
              ariaLabel={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_MEETING_HOST()}
              placeholder={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_MEETING_HOST()}
            />
          )}
          <ZoomHostValidator
            formName={`${name}.alternativeHosts`}
            onValidHost={(isValid) => setIsValidAlternativeHos(isValid)}
            disabled={isZoomHostDisabled}
            label={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS()}
            ariaLabel={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS()}
            placeholder={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS_EXAMPLE()}
            className='alternative-hosts'
            data-qa={config.pendo.liveEvent.liveEventSession.zoom.alternativeHost}
            description={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS_DESCRIPTION()}
            isAlternativeHost
            host={validAlternativeHosts.current}
            tooltip={isContentManagementCollection ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP() : ''}
          />
        </React.Fragment>
      )}
      {sessionType === SessionTypes.EXTERNAL && (
        <div>
          <label className={labelClass}>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN()}</label>
          <NvTooltip
            text={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()}
            placement='top'
            enabled={isContentManagementCollection}
          >
            <div>
              <NvTextInput
                withForm
                required
                autoComplete='off'
                errorOnTouching={false}
                className='link-to-join'
                name={`${name}.joinUrl`}
                disabled={disabled || isContentManagementCollection}
                data-qa={config.pendo.liveEvent.liveEventSession.external.joinUrl}
                ariaLabel={isContentManagementCollection
                  ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()
                  : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN()}
                placeholder={isContentManagementCollection
                  ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.SETUP_IN_COURSE_TOOLTIP()
                  : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.LINK_TO_JOIN_PLACEHOLDER()}
              />
            </div>
          </NvTooltip>
        </div>
      )}
      {canDelete && (
        <div className='d-flex mt-4 justify-content-end'>
          <span
            className='d-flex text-small gray-3 cursor-pointer'
            onClick={deleteSession}
            data-qa={config.pendo.liveEvent.liveEventSession.removeSession}
          >
            {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.REMOVE_SESSION()}
            <NvIcon icon='trash' size='smallest' className='cursor-pointer ml-2' />
          </span>
        </div>
      )}
    </div>
  );
};

export default LiveEventSession;
